import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { FiMoreVertical } from 'react-icons/fi'
import { FaRegTrashCan } from 'react-icons/fa6'
import { WiMoonAltNew } from 'react-icons/wi'
import requestStore from '../../stores/requestStore'
import { requestColorStyles } from '../../theme/requestColorTokens'
import ColorTokenPicker from './ColorTokenPicker'
import RequestDetailDialog from './RequestDetailDialog'
import ResourceAssignmentControl from '../organization/ResourceAssignmentControl'
import { canMutateResource } from '../../tenancy/resourcePermissions'

export default function RequestCard({
  title,
  description,
  id,
  colorToken,
  columnId,
  boardId,
  addressShort,
  extraFilter,
  jsonFileName,
  countryCodeISO,
  uuid,
  outcome = 'OPEN',
  closedAt,
  sourceId,
  clientId,
  sourceName,
  assigneeMembershipId,
  permissions,
}) {
  const theme = useTheme()
  const colors = requestColorStyles(colorToken, theme.palette.mode)
  const [anchorEl, setAnchorEl] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [wasViewed, setWasViewed] = useState(false)

  const request = requestStore.requests
    .flatMap(board => board?.columns?.flatMap(column => column?.requests || []) || [])
    .find(item => item?.requestId === id)
  const hasNewObjects = !wasViewed && Boolean(request?.objects?.some(object => object?.status === 'new'))
  const mayMutate = canMutateResource({ permissions })

  const stop = callback => event => {
    event.stopPropagation()
    callback(event)
  }

  const closeOutcome = async (event, nextOutcome) => {
    event.stopPropagation()
    setAnchorEl(null)
    await requestStore.closeRequest(id, nextOutcome)
  }

  const chooseColor = async nextToken => {
    await requestStore.updateRequestColor(id, nextToken)
    setAnchorEl(null)
  }

  const assign = async membershipId => {
    await requestStore.assignRequest(id, membershipId)
  }

  return (
    <>
      <Card
        onClick={() => setModalOpen(true)}
        sx={{
          minWidth: 225,
          maxWidth: 300,
          mt: 2,
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: colors?.background || 'background.paper',
          color: colors?.text || 'text.primary',
          border: '1px solid',
          borderColor: colors?.border || 'divider',
          transition: theme.transitions.create(['box-shadow', 'border-color', 'transform']),
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: colors?.border || alpha(theme.palette.text.primary, 0.35),
            transform: 'translateY(-2px)',
          },
        }}
      >
        {hasNewObjects && <WiMoonAltNew style={{ position: 'absolute', top: 8, right: 8, color: '#22c55e', fontSize: 14 }} />}
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'inherit' }}>
              {title}
            </Typography>
            {mayMutate && <IconButton size="small" aria-label="Действия заявки" onClick={stop(event => setAnchorEl(event.currentTarget))} sx={{ color: 'inherit' }}>
              <FiMoreVertical />
            </IconButton>}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {outcome !== 'OPEN' && <Chip size="small" color={outcome === 'WON' ? 'success' : 'error'} label={outcome === 'WON' ? 'Успешно' : 'Неуспешно'} />}
            {sourceName && <Chip size="small" variant="outlined" label={sourceName} />}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', color: colors?.text || 'text.secondary' }}>
            {description}
          </Typography>
          <Box sx={{ mt: 1 }}><ResourceAssignmentControl assigneeMembershipId={assigneeMembershipId} onAssign={assign} disabled={!permissions?.canAssign} /></Box>
          {mayMutate && outcome === 'OPEN' && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
              <Button size="small" color="success" onClick={event => closeOutcome(event, 'WON')}>Закрыть успешно</Button>
              <Button size="small" color="error" onClick={event => closeOutcome(event, 'LOST')}>Закрыть неуспешно</Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {mayMutate && <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <Box onClick={event => event.stopPropagation()}><ColorTokenPicker value={colorToken} onChange={chooseColor} /></Box>
        <MenuItem onClick={stop(() => { setAnchorEl(null); setDeleteOpen(true) })}>
          <FaRegTrashCan style={{ marginRight: 10 }} />Удалить
        </MenuItem>
      </Menu>}

      {mayMutate && <Dialog open={deleteOpen} onClose={stop(() => setDeleteOpen(false))}>
        <DialogTitle>Удалить заявку?</DialogTitle>
        <DialogContent><DialogContentText>Заявка будет перемещена в архив. Её можно восстановить без изменения результата и колонки.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={stop(() => setDeleteOpen(false))}>Отмена</Button>
          <Button color="error" onClick={stop(async () => { setDeleteOpen(false); await requestStore.archiveRequest(id) })}>Удалить</Button>
        </DialogActions>
      </Dialog>}

      <RequestDetailDialog open={modalOpen} onClose={() => { setModalOpen(false); setWasViewed(true) }} request={{ id, boardId, columnId, description, title, addressShort, filter: extraFilter, jsonFileName, countryCodeISO, uuid, outcome, closedAt, sourceId, clientId, permissions }} />
    </>
  )
}
